import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { useOrgInvitations, type Invitation } from "@/hooks/useOrgInvitations";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { RefreshCw, Check, Trash2 } from "lucide-react";

export function NoOrganizationPage() {
  const navigate = useNavigate();
  const { invitations, isPending, refetch } = useOrgInvitations();
  const { data: session } = authClient.useSession();
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleRefresh = () => {
    void refetch();
  };

  const handleAccept = async (invitation: Invitation) => {
    setProcessingId(invitation.id);
    try {
      const { error } = await authClient.organization.acceptInvitation({
        invitationId: invitation.id,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success("Invitation acceptée");

      // Set as active organization
      await authClient.organization.setActive({
        organizationId: invitation.organizationId,
      });

      navigate("/");
    } catch (error) {
      toast.error("Erreur lors de l'acceptation", {
        description: (error as Error).message,
      });
      setProcessingId(null);
    }
  };

  const handleReject = async (invitationId: string) => {
    setProcessingId(invitationId);
    try {
      const { error } = await authClient.organization.rejectInvitation({
        invitationId,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success("Invitation refusée");
      await refetch();
    } catch (error) {
      toast.error("Erreur lors du refus", {
        description: (error as Error).message,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreateOrg = useCallback(async () => {
    if (!session) {
      navigate("/login", { replace: true });
      return;
    }

    try {
      await authClient.updateUser({ onboarding: "step3" });

      navigate("/onboarding/step3", { replace: true });
    } catch (error) {
      toast.error("Impossible de relancer la création d'organisation", {
        description: (error as Error).message,
      });
    }
  }, [navigate, session]);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <Card className="relative z-10 w-full max-w-xl bg-white/85">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Bienvenue sur Nautika</CardTitle>
          <CardDescription className="text-center">Vous n'êtes actuellement membre d'aucun club.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-medium">Invitations reçues</h2>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isPending}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isPending ? "animate-spin" : ""}`} />
                {isPending ? "Chargement" : "Actualiser"}
              </Button>
            </div>

            {isPending ? (
              <Skeleton className="h-13.5 w-full" />
            ) : !invitations || invitations.length === 0 ? (
              <p className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">Aucune invitation trouvée pour le moment.</p>
            ) : (
              <ul className="space-y-3">
                {invitations.map((invitation: Invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium">
                        Invité à {invitation.organizationName} par {invitation.inviterName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Expire le{" "}
                        {invitation.expiresAt.toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        à {invitation.expiresAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        className="h-8 text-green-600 hover:bg-green-50 hover:text-green-700"
                        onClick={() => handleAccept(invitation)}
                        disabled={processingId === invitation.id}
                        title="Rejoindre le club"
                      >
                        <Check className="h-4 w-4" />
                        Rejoindre le club
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleReject(invitation.id)}
                        disabled={processingId === invitation.id}
                        title="Refuser l'invitation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border-t pt-4">
            <h2 className="text-sm font-medium">Créer un club</h2>
            <p className="mt-1 text-sm text-muted-foreground">Vous pouvez également lancer la création d'un nouveau club.</p>
            <div className="mt-10 flex justify-center">
              <Button onClick={handleCreateOrg}>Créer un club</Button>
            </div>
          </section>
        </CardContent>
      </Card>
    </div>
  );
}
